/**
 * SKILLCARDS payantes (migration 0304) — types des RPC de prix et helpers PURS.
 *
 * ⚠️ La SOURCE DE VÉRITÉ des prix est SQL (`skillcard_quote`) : le checkout
 * Stripe envoie le montant calculé par la base. `previewSkillcardPrice` n'est
 * qu'un MIROIR pour l'aperçu en direct de l'admin — garder synchro avec
 * `skillcard_quote` (cf. tests de parité dans pricing.test.ts).
 */

export type SkillcardOfferKind = 'card' | 'pack' | 'programme';

export interface SkillcardPackTier {
  min_cards: number;
  discount_pct: number;
}

export interface SkillcardPricingSettings {
  unit_price_cents: number;
  pack_tiers: SkillcardPackTier[];
  programme_discount_pct: number;
  /** Plafond du prix d'un programme (centimes, migration 0507) ; null = aucun. */
  programme_max_cents?: number | null;
  round_step_cents: number;
  validity_months: number;
  free_sample_questions: number;
}

/** Devis renvoyé par `skillcard_quote` (via get_skillcard_offers / grid). */
export interface SkillcardQuote {
  kind: SkillcardOfferKind;
  target_id: string;
  label: string;
  level: string | null;
  subject: string | null;
  total_cards: number;
  cards_to_buy: number;
  card_ids: string[];
  price_cents: number;
  unit_total_cents: number;
  savings_pct: number;
  is_override: boolean;
  /** Cartes déduites du prix (plafond 2 cartes pour un pack — migration 0320). */
  deducted_cards?: number;
  /** Packs déduits du prix du programme (plafond 2 — migration 0320). */
  deducted_packs?: number;
  /** Exercices publiés reliés aux cartes du lot (migration 0504). */
  exercise_count?: number;
  /** Questions reliées aux cartes du lot (migration 0504). */
  question_count?: number;
  /** Promo en cours appliquée au prix (migration 0717) : prix d'avant, %, fin. */
  promo?: SkillcardQuotePromo | null;
}

export interface SkillcardQuotePromo {
  percent: number;
  label: string;
  ends_at: string;
  before_cents: number;
}

export interface SkillcardAccess {
  signed_in: boolean;
  allowed: boolean;
  /** `assigned` : carte offerte par un prof lié (migration 0353), accès complet. */
  reason: 'role' | 'subscription' | 'pass' | 'skillcard' | 'assigned' | 'no_access';
  owned: boolean;
  /** Carte assignée par un prof (accès gratuit tant que l'assignation existe). */
  assigned?: boolean;
  deck_expires_at: string | null;
  free_exercise_id: string | null;
  free_questions: number;
}

export interface SkillcardOffers {
  card: SkillcardQuote;
  packs: SkillcardQuote[];
  programmes: SkillcardQuote[];
  access: SkillcardAccess;
  settings: Pick<
    SkillcardPricingSettings,
    'unit_price_cents' | 'validity_months' | 'free_sample_questions'
  >;
}

export interface SkillcardGridPack extends SkillcardQuote {
  slug: string;
  override_cents: number | null;
}

export interface SkillcardGridProgramme extends SkillcardQuote {
  title: string;
  override_cents: number | null;
  packs: SkillcardGridPack[];
}

export interface SkillcardPriceGrid {
  settings: SkillcardPricingSettings;
  programmes: SkillcardGridProgramme[];
}

export const SKILLCARD_STRIPE_MIN_CENTS = 50;

/** « 1,90 € » (espace insécable avant €). */
export function formatEuros(cents: number): string {
  const v = (Math.round(cents) / 100).toFixed(2).replace('.', ',');
  return `${v.replace(/,00$/, '')}\u00a0€`;
}

/** Prix moyen d'une carte dans l'offre (cartes encore à acheter). */
export function perCardCents(q: Pick<SkillcardQuote, 'price_cents' | 'cards_to_buy'>): number {
  return q.cards_to_buy > 0 ? Math.round(q.price_cents / q.cards_to_buy) : 0;
}

/** Économie (centimes) d'une offre par rapport à l'achat carte par carte. */
export function offerSavingsCents(
  q: Pick<SkillcardQuote, 'price_cents' | 'unit_total_cents' | 'cards_to_buy'>,
): number {
  return q.cards_to_buy > 0 ? Math.max(0, q.unit_total_cents - q.price_cents) : 0;
}

/**
 * Offres à proposer dans le paywall : la carte, puis le pack achetable le moins
 * cher, puis le programme achetable le moins cher (pack/programme seulement s'ils
 * contiennent plus d'une carte à acheter).
 */
export function paywallOffers(
  offers: Pick<SkillcardOffers, 'card' | 'packs' | 'programmes'>,
): SkillcardQuote[] {
  const cheapest = (list: SkillcardQuote[]) =>
    list
      .filter((q) => isOfferPurchasable(q) && q.cards_to_buy > 1)
      .sort((a, b) => a.price_cents - b.price_cents)[0];
  return [offers.card, cheapest(offers.packs), cheapest(offers.programmes)].filter(
    (q): q is SkillcardQuote => !!q && isOfferPurchasable(q),
  );
}

export function isOfferPurchasable(
  q: Pick<SkillcardQuote, 'price_cents' | 'cards_to_buy'> | null | undefined,
): boolean {
  return !!q && q.cards_to_buy > 0 && q.price_cents >= SKILLCARD_STRIPE_MIN_CENTS;
}

/** Remise du palier le plus haut atteint (miroir `skillcard_pack_discount_pct`). */
export function packDiscountPct(tiers: SkillcardPackTier[], cardCount: number): number {
  return tiers.reduce(
    (best, t) => (t.min_cards <= cardCount ? Math.max(best, t.discount_pct) : best),
    0,
  );
}

/** Arrondi PostgreSQL `round(numeric)` : moitié loin de zéro. */
function pgRound(x: number): number {
  return Math.sign(x) * Math.round(Math.abs(x) + 1e-9);
}

/**
 * MIROIR de `skillcard_quote` (prix d'une offre). `cardsToBuy` défaut =
 * totalCards (rien de possédé).
 */
export function previewSkillcardPrice(
  settings: Pick<
    SkillcardPricingSettings,
    | 'unit_price_cents'
    | 'pack_tiers'
    | 'programme_discount_pct'
    | 'round_step_cents'
    | 'programme_max_cents'
  >,
  kind: SkillcardOfferKind,
  totalCards: number,
  opts: { cardsToBuy?: number; overrideCents?: number | null } = {},
): number {
  if (totalCards <= 0) return 0;
  const n = opts.cardsToBuy ?? totalCards;
  if (n <= 0) return 0;
  const disc =
    kind === 'card'
      ? 0
      : kind === 'pack'
        ? packDiscountPct(settings.pack_tiers, totalCards)
        : Math.max(
            settings.programme_discount_pct,
            packDiscountPct(settings.pack_tiers, totalCards),
          );
  const overridden = kind !== 'card' && opts.overrideCents != null;
  let base = overridden
    ? (opts.overrideCents as number)
    : totalCards * settings.unit_price_cents * (1 - disc / 100);
  // Plafond d'un programme (0507), sauf prix forcé par l'admin.
  if (kind === 'programme' && !overridden && settings.programme_max_cents != null) {
    base = Math.min(base, settings.programme_max_cents);
  }
  const step = settings.round_step_cents;
  return Math.max(SKILLCARD_STRIPE_MIN_CENTS, pgRound((base * n) / totalCards / step) * step);
}

/** Paliers valides : min_cards ≥ 2 croissants et uniques, remise 0..90. */
export function validatePackTiers(tiers: SkillcardPackTier[]): string | null {
  const seen = new Set<number>();
  for (const t of tiers) {
    if (!Number.isInteger(t.min_cards) || t.min_cards < 2)
      return 'Chaque palier commence à 2 cartes au moins.';
    if (!(t.discount_pct >= 0 && t.discount_pct <= 90))
      return 'Une remise doit être entre 0 et 90 %.';
    if (seen.has(t.min_cards)) return 'Deux paliers ont le même nombre de cartes.';
    seen.add(t.min_cards);
  }
  return null;
}

export interface DeckStatus {
  active: boolean;
  /** Jours restants (0 si expiré / absent). */
  daysLeft: number;
}

export function deckStatus(
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): DeckStatus {
  if (!expiresAt) return { active: false, daysLeft: 0 };
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (!(ms > 0)) return { active: false, daysLeft: 0 };
  return { active: true, daysLeft: Math.ceil(ms / 86_400_000) };
}

/** « 6 octobre 2027 ». */
export function formatDeckDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Libellé de la raison d'accès (page carte / page d'achat). */
export function skillcardAccessLabel(reason: SkillcardAccess['reason']): string | null {
  switch (reason) {
    case 'subscription':
      return 'Incluse dans ton abonnement';
    case 'pass':
      return 'Incluse dans ton pass en cours';
    case 'skillcard':
      return 'Dans ton deck';
    case 'assigned':
      return 'Offerte par ton prof : accès complet';
    case 'role':
      return 'Accès complet (ta carte ou admin)';
    default:
      return null;
  }
}

// ── Déduction de ce qui est déjà acheté (migration 0320) ─────────────────────
// Un pack ne déduit au plus que 2 SKILLCARDS déjà possédées ; un programme ne
// déduit au plus que 2 PACKS déjà possédés (les 2 plus gros). MIROIR du calcul
// de `skillcard_quote` — garder synchro.

export const SKILLCARD_MAX_DEDUCTED = 2;

/** Cartes FACTURÉES d'un lot, une fois la déduction plafonnée appliquée. */
export function chargedCardCount(
  kind: SkillcardOfferKind,
  totalCards: number,
  owned: { cardsInLot?: number; ownedPackSizes?: number[] } = {},
): number {
  if (kind === 'pack') {
    return Math.max(0, totalCards - Math.min(SKILLCARD_MAX_DEDUCTED, owned.cardsInLot ?? 0));
  }
  if (kind === 'programme') {
    const deducted = [...(owned.ownedPackSizes ?? [])]
      .sort((a, b) => b - a)
      .slice(0, SKILLCARD_MAX_DEDUCTED)
      .reduce((a, b) => a + b, 0);
    return Math.max(0, totalCards - deducted);
  }
  return totalCards;
}

/** Mention de la réduction affichée sous une offre de pack ou de programme. */
export function deductionNote(
  quote: Pick<SkillcardQuote, 'kind' | 'deducted_cards' | 'deducted_packs'>,
): string | null {
  if (quote.kind === 'pack') {
    const n = quote.deducted_cards ?? 0;
    return n > 0
      ? `Tu as déjà ${n} carte${n > 1 ? 's' : ''} de ce pack : ${n > 1 ? 'elles sont déduites' : 'elle est déduite'} du prix (jusqu’à 2 SKILLCARDS).`
      : 'Prix réduit si tu as déjà des cartes de ce pack (jusqu’à 2 SKILLCARDS déduites).';
  }
  if (quote.kind === 'programme') {
    const n = quote.deducted_packs ?? 0;
    return n > 0
      ? `${n > 1 ? `Tes ${n} packs déjà achetés sont déduits` : 'Ton pack déjà acheté est déduit'} du prix (jusqu’à 2 packs).`
      : 'Prix réduit si tu as déjà acheté des packs de ce programme (jusqu’à 2 packs déduits).';
  }
  return null;
}

/**
 * Contenu d'une offre : « 1 carte · 12 exos · 30 questions ». Les nombres
 * d'exercices et de questions viennent du devis (absents d'un ancien devis :
 * on ne montre alors que les cartes).
 */
export function offerContentLabel(
  q: Pick<SkillcardQuote, 'total_cards' | 'exercise_count' | 'question_count'>,
): string {
  const count = (n: number, word: string) =>
    `${n.toLocaleString('fr-FR')} ${word}${n > 1 ? 's' : ''}`;
  const parts = [count(q.total_cards, 'carte')];
  if (q.exercise_count && q.exercise_count > 0) parts.push(count(q.exercise_count, 'exo'));
  if (q.question_count && q.question_count > 0) parts.push(count(q.question_count, 'question'));
  return parts.join(' · ');
}
